import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiService, { refreshAccessTokenSilently } from '../../utils/apiService';
import { getAuthToken } from '../../utils/authUtils';
import { calculateGasFeesFromBalanceForEngine } from '../../config/engine-gas-config';

const calculateGasLevel = (totalBalance, engine = null) => {
    const gas = calculateGasFeesFromBalanceForEngine(engine, totalBalance);
    const bal = Number(totalBalance);
    const firstTime = !Number.isFinite(bal) || bal === 0;
    return { ...gas, firstTime };
};

// New thunk to fetch gas level for a specific bot
export const fetchGasLevel = createAsyncThunk(
    'updateGas/fetchGasLevel',
    async (payload, { rejectWithValue: _rejectWithValue }) => {
        const { botId: rawBotId, totalBalance: tb, gasAmount: legacyGa, forceRefresh, cachedOnly, engine } = payload || {};
        const botId =
            rawBotId !== undefined && rawBotId !== null ? String(rawBotId) : rawBotId;
        const totalBalance =
            tb !== undefined && tb !== null ? tb : legacyGa !== undefined && legacyGa !== null ? legacyGa : 0;

        try {
            // console.log('fetchGasLevel called with:', { botId, totalBalance });
            
            if (!getAuthToken()) {
                await refreshAccessTokenSilently();
            }

            // console.log('Making API call to update gas fees...');

            // Call the API to get actual gas level data
            const response = await apiService.post(`/bots/${botId}/update-gas-fees`, {
                gasAmount: totalBalance,
                ...(forceRefresh ? { forceRefresh: true } : {}),
                ...(cachedOnly ? { cachedOnly: true } : {}),
            });
            const data = response?.data;
            // console.log('API response data:', data);

            // Use the API response or calculate locally if API doesn't return gasFees
            const gasFees = data?.gasFees || calculateGasLevel(totalBalance, engine);
            const serverTotalRaw = data?.totalBalance ?? data?.gasFees?.totalBalance;
            const serverTotal =
              serverTotalRaw !== undefined && serverTotalRaw !== null && Number.isFinite(Number(serverTotalRaw))
                ? Number(serverTotalRaw)
                : totalBalance;
            // console.log('Final gas fees:', gasFees);

            return {
                botId: botId,
                gasFees: gasFees,
                totalBalance: serverTotal,
                message: data?.message || 'Gas level fetched successfully'
            };
        }
        catch (error) {
            const detail =
                error && typeof error === "object" && "message" in error
                    ? `${error.message}${error.status != null ? ` [${error.status}]` : ""}`
                    : String(error);
            // API failed but UI still gets gas from local calc — warn so Next dev overlay does not show `{}` for Error/plain API errors
            console.warn("Gas level API failed, using local fallback:", detail);
            // Must pass engine — GLOBAL bands treat ~8 SOL as full (level 10); V12 needs ~162.
            const gasFees = calculateGasLevel(totalBalance, engine);
            return {
                botId: botId,
                gasFees: gasFees,
                totalBalance: totalBalance,
                message: 'Gas level calculated locally'
            };
        }
    },
    {
        condition: (payload, { getState }) => {
            const botId =
                payload?.botId !== undefined && payload?.botId !== null
                    ? String(payload.botId)
                    : '';
            if (!botId) return false;
            return !getState().updateGas.gasFetchPending?.[botId];
        },
    },
);

const initialState = {
    data: null,
    loading: 'idle',
    error: null,
    message: null,
    gasLevels: {}, // Store gas levels for each bot
    gasFetchPending: {}, // botId -> true while update-gas-fees is in flight
};

const updateGasSlice = createSlice({
    name: 'updateGas',
    initialState,
    reducers: {
        resetUpdateGas(state) {
            state.data = null;
            state.loading = 'idle';
            state.error = null;
            state.message = null;
        },
        updateBotGasLevel(state, action) {
            const { botId: rawId, totalBalance, engine } = action.payload;
            const botId = rawId !== undefined && rawId !== null ? String(rawId) : rawId;
            const gasFees = calculateGasLevel(totalBalance, engine);
            state.gasLevels[botId] = {
                ...gasFees,
                totalBalance
            };
        },
        /** Persist server `gasFees` from POST /recharge (or GET /bots) so the card updates without a full page reload. */
        applyRechargeGasFromServer(state, action) {
            const { botId: rawId, gasFees, totalBalance } = action.payload || {};
            if (!gasFees || typeof gasFees !== "object") return;
            const botId =
                rawId !== undefined && rawId !== null ? String(rawId) : "";
            if (!botId) return;
            const tbRaw =
                totalBalance !== undefined && totalBalance !== null
                    ? totalBalance
                    : gasFees.totalBalance;
            const tb = Number(tbRaw);
            state.gasLevels[botId] = {
                ...gasFees,
                totalBalance: Number.isFinite(tb) ? tb : gasFees.totalBalance ?? 0,
            };
        },
        clearGasLevels(state) {
            state.gasLevels = {};
        }
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchGasLevel.pending, (state, action) => {
                const botId =
                    action.meta.arg?.botId !== undefined &&
                    action.meta.arg?.botId !== null
                        ? String(action.meta.arg.botId)
                        : '';
                if (botId) {
                    state.gasFetchPending[botId] = true;
                }
            })
            .addCase(fetchGasLevel.fulfilled, (state, action) => {
                const { botId: rawId, gasFees, totalBalance } = action.payload;
                const botId =
                    rawId !== undefined && rawId !== null ? String(rawId) : rawId;
                const forceRefresh = action.meta.arg?.forceRefresh === true;
                const existing = botId ? state.gasLevels[botId] : null;
                const unfundedGas =
                    existing?.firstTime === true || gasFees?.firstTime === true;
                // Only ignore a lower *balance* (stale cache race). Never block a level
                // drop when balance is same/higher — that corrects wrong GLOBAL-band paints.
                if (!forceRefresh && !unfundedGas && existing && gasFees) {
                    const exTot = Number(existing.totalBalance);
                    const inTot = Number(totalBalance ?? gasFees.totalBalance);
                    const totalsComparable =
                        Number.isFinite(exTot) && Number.isFinite(inTot);
                    if (totalsComparable && inTot < exTot - 1e-9) {
                        if (botId) {
                            delete state.gasFetchPending[botId];
                        }
                        return;
                    }
                }
                state.gasLevels[botId] = {
                    ...gasFees,
                    totalBalance
                };
                if (botId) {
                    delete state.gasFetchPending[botId];
                }
            })
            .addCase(fetchGasLevel.rejected, (state, action) => {
                const botId =
                    action.meta.arg?.botId !== undefined &&
                    action.meta.arg?.botId !== null
                        ? String(action.meta.arg.botId)
                        : '';
                if (botId) {
                    delete state.gasFetchPending[botId];
                }
            });
    }
});

export const {
    resetUpdateGas,
    updateBotGasLevel,
    applyRechargeGasFromServer,
    clearGasLevels,
} = updateGasSlice.actions;

// Selectors
export const selectGasLevel = (state, botId) => state.updateGas.gasLevels[botId];
export const selectAllGasLevels = (state) => state.updateGas.gasLevels;
export const selectGasLoading = (state) => state.updateGas.loading;
export const selectGasError = (state) => state.updateGas.error;

export default updateGasSlice.reducer;